// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ellipsis, iconLinkCommonStyles, maxLengthAttr, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
}

type Props = OwnProps & HasCartLineContext;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    /** @deprecated */
    toggleLink?: LinkPresentationProps;
    label?: TypographyPresentationProps;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    showNotesLink?: LinkPresentationProps;
    notesModalWrapper?: InjectableCss;
    saveButton?: ButtonPresentationProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    editableNotesTextArea: {
        mergeCss: true,
        cssOverrides: {
            inputSelect: css`
                max-height: 340px;
                resize: vertical;
                -webkit-line-clamp: unset !important;
            `,
        },
    },
    label: {
        css: css`
            ${xScreen("lt", "md")} {
                width: 100%;
                display: flex;
                justify-content: space-between;
                flex-wrap: wrap;
            }
        `,
    },
    showNotesLink: {
        ...mergeWithCss(iconLinkCommonStyles(File), {
            css: css`
                white-space: nowrap;
            `,
        }),
    },
    notesModalWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    saveButton: {
        variant: "primary",
        css: css`
            margin-top: 15px;
            margin-left: auto;
        `,
    },
    readOnlyNotesText: {
        extendedStyles: {
            heading: {
                css: css`
                    text-transform: capitalize;
                    min-width: 65px;
                    line-height: 18px;
                `,
            },
            text: {
                css: css`
                    padding-right: 10px;
                    ${ellipsis(4)}
                    line-height: 18px;
                `,
            },
        },
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    onNotesChange = () => {},
    label,
    extendedStyles,
}) => {
    const [showLineNotesModel, setShowLineNotesModel] = useState(false);
    const lineNotesClickHandler = () => {
        setShowLineNotesModel(!showLineNotesModel);
    };

    const [notes, setNotes] = useState(cartLine.notes);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = () => {
        onNotesChange(notes);
        setShowLineNotesModel(false);
    };

    const heading = showLineNotesModel
        ? "Hide Line Notes"
        : cartLine.notes.length === 0
          ? "Add Line Notes"
          : "Show Line Notes";

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography
                    {...styles.label}
                    htmlFor={`notes-${cartLine.id}`}
                    forwardAs="label"
                    data-test-selector="cartline_notesShowHideButton"
                >
                    <Link onClick={lineNotesClickHandler} {...styles.showNotesLink}>
                        {translate(heading)}
                    </Link>
                </Typography>
                {/* XNG-Changes: Show TextArea in modal */}
                <Modal
                    headline={translate("Line Notes")}
                    isOpen={showLineNotesModel}
                    closeOnEsc={false}
                    handleClose={() => setShowLineNotesModel(false)}
                    sizeVariant="medium"
                >
                    <StyledWrapper {...styles.notesModalWrapper}>
                        <TextArea
                            {...styles.editableNotesTextArea}
                            placeholder={translate("Enter Your Notes")}
                            value={notes}
                            id={`notes-${cartLine.id}`}
                            disabled={!cart.canModifyOrder}
                            onChange={changeHandler}
                            maxLength={maxLengthAttr.note}
                            data-test-selector="cartline_notes"
                        />
                        <Button name={translate("Save Notes")} onClick={blurHandler} {...styles.saveButton}>
                            {translate("Save")}
                        </Button>
                    </StyledWrapper>
                </Modal>
            </StyledWrapper>
        );
    }

    if (!cartLine.notes) {
        return null;
    }

    return (
        <SmallHeadingAndText
            {...styles.readOnlyNotesText}
            heading={label || `${translate("Item Notes")}:`}
            text={cartLine.notes}
        />
    );
};

export default withCartLine(CartLineNotes);
